import React, { useEffect, useState } from 'react';
import { authHeaders } from '../../services/auth';
import { Globe, MousePointerClick, Mail, Smartphone, TrendingDown, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const LandingAnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/landing/analytics?days=${d}`, { headers: authHeaders() });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      } else {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  if (loading && !data) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-white p-6"
        data-testid="landing-analytics-loading"
      >
        <div className="text-sm text-slate-500">Loading landing analytics…</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-6"
      data-testid="landing-analytics-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#14D4C6]" />
          <h3 className="text-base font-semibold text-slate-900">Landing Page Analytics</h3>
          <span className="text-xs text-slate-500">(last {days} days)</span>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                days === d
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`la-days-${d}`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => load(days)}
            className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
            title="Refresh"
            data-testid="la-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="la-tiles">
        <Tile icon={<Globe className="w-4 h-4" />} label="Page views" value={data.total_views} testid="la-tile-views" />
        <Tile icon={<MousePointerClick className="w-4 h-4" />} label="Unique visitors" value={data.unique_visitors} testid="la-tile-visitors" />
        <Tile icon={<Mail className="w-4 h-4" />} label="Demo requests" value={data.form_submits} testid="la-tile-leads" highlight />
        <Tile icon={<Smartphone className="w-4 h-4" />} label="Install clicks" value={data.install_clicks} testid="la-tile-installs" />
        <Tile icon={<TrendingDown className="w-4 h-4" />} label="Bounce rate" value={`${data.bounce_rate_pct}%`} testid="la-tile-bounce" />
        <Tile icon={<Smartphone className="w-4 h-4" />} label="Mobile share" value={`${data.mobile_share_pct}%`} testid="la-tile-mobile" />
        <Tile label="Login clicks" value={data.login_clicks} testid="la-tile-logins" />
        <Tile label="Sessions" value={data.unique_sessions} testid="la-tile-sessions" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div data-testid="la-top-sections">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Most viewed sections</h4>
          {data.top_sections.length ? (
            <ul className="space-y-1.5">
              {data.top_sections.map((s) => (
                <li key={s.section} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 font-mono text-xs">#{s.section}</span>
                  <span className="text-slate-500">
                    <strong className="text-slate-900">{s.unique}</strong> visitors
                    <span className="text-slate-400"> · {s.count} views</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">No section views yet.</p>
          )}
        </div>
        <div data-testid="la-top-clicks">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Top clicks</h4>
          {data.top_clicks.length ? (
            <ul className="space-y-1.5">
              {data.top_clicks.map((c) => (
                <li key={c.target} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 font-mono text-xs">{c.target}</span>
                  <span className="text-slate-900 font-semibold">{c.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">No clicks yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Tile = ({ icon, label, value, testid, highlight }) => (
  <div
    className={`rounded-lg border px-3 py-2.5 ${
      highlight ? 'bg-[#14D4C6]/10 border-[#14D4C6]/30' : 'bg-slate-50 border-slate-200'
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-0.5">
      {icon}
      <span className="truncate">{label}</span>
    </div>
    <div className="text-xl font-bold text-slate-900 tabular-nums">{value}</div>
  </div>
);
